import { ReactNode, forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { classNames } from '@/modules/shared/utils/classNames'

interface ScrollShadowWrapperProps {
  children: ReactNode
  className?: string
  style?: React.CSSProperties
  direction?: 'horizontal' | 'vertical'
  /** When true with horizontal direction, use overflow-auto so the area can scroll vertically (e.g. viewport-capped tables). */
  overflowBothAxes?: boolean
  dragScrollingEnabled?: boolean
  testId?: string
  id?: string
}

const ScrollShadowWrapper = forwardRef<HTMLDivElement, ScrollShadowWrapperProps>(function ScrollShadowWrapper(
  {
    children,
    className = '',
    style = {},
    direction = 'vertical',
    overflowBothAxes = false,
    dragScrollingEnabled,
    testId,
    id,
  },
  forwardedRef
) {
  const [scrollMetrics, setScrollMetrics] = useState({
    scrollStart: 0,
    scrollSize: 0,
    clientSize: 0,
  })

  // controls for click and drag scrolling.
  const [isDown, setIsDown] = useState(false)
  const [startX, setStartX] = useState(0)

  const onMouseDown = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (!wrapperRef.current) return
    setIsDown(true)
    setStartX(e.pageX - wrapperRef.current.offsetLeft)
  }

  const onMouseLeave = () => {
    setIsDown(false)
  }

  const onMouseUp = () => {
    setIsDown(false)
  }

  const updateScrollMetrics = useCallback(
    ({ scrollStart, scrollSize, clientSize }: { scrollStart: number; scrollSize: number; clientSize: number }) => {
      setScrollMetrics((previousMetrics) => {
        if (
          previousMetrics.scrollStart === scrollStart &&
          previousMetrics.scrollSize === scrollSize &&
          previousMetrics.clientSize === clientSize
        ) {
          return previousMetrics
        }

        return {
          scrollStart,
          scrollSize,
          clientSize,
        }
      })
    },
    []
  )

  const onMouseMove = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (!isDown || !wrapperRef.current || !dragScrollingEnabled) return
    e.preventDefault()
    const x = e.pageX - wrapperRef.current.offsetLeft
    const walk = (x - startX) / 10 // slow the scroll
    wrapperRef.current.scrollLeft = scrollMetrics.scrollStart - walk
    updateScrollMetrics({
      scrollStart: wrapperRef.current.scrollLeft,
      scrollSize: wrapperRef.current.scrollWidth,
      clientSize: wrapperRef.current.clientWidth,
    })
  }

  const onScrollHandler = (event: React.UIEvent<HTMLDivElement>) => {
    if (direction === 'vertical') {
      updateScrollMetrics({
        scrollStart: event.currentTarget.scrollTop,
        scrollSize: event.currentTarget.scrollHeight,
        clientSize: event.currentTarget.clientHeight,
      })
    } else {
      updateScrollMetrics({
        scrollStart: event.currentTarget.scrollLeft,
        scrollSize: event.currentTarget.scrollWidth,
        clientSize: event.currentTarget.clientWidth,
      })
    }
  }

  const wrapperRef = useRef<HTMLDivElement>(null)

  const setWrapperRef = useCallback(
    (el: HTMLDivElement | null) => {
      wrapperRef.current = el
      if (typeof forwardedRef === 'function') {
        forwardedRef(el)
      } else if (forwardedRef) {
        forwardedRef.current = el
      }
    },
    [forwardedRef]
  )

  const resetRefSizes = useCallback(() => {
    if (!wrapperRef.current) return

    if (direction === 'vertical') {
      updateScrollMetrics({
        scrollStart: wrapperRef.current.scrollTop,
        scrollSize: wrapperRef.current.scrollHeight,
        clientSize: wrapperRef.current.clientHeight,
      })
    } else {
      updateScrollMetrics({
        scrollStart: wrapperRef.current.scrollLeft,
        scrollSize: wrapperRef.current.scrollWidth,
        clientSize: wrapperRef.current.clientWidth,
      })
    }
  }, [direction, updateScrollMetrics])

  useEffect(() => {
    resetRefSizes()

    const handleResize = () => {
      if (
        wrapperRef.current &&
        ((direction === 'vertical' && wrapperRef.current.scrollHeight <= wrapperRef.current.clientHeight) ||
          (direction === 'horizontal' && wrapperRef.current.scrollWidth <= wrapperRef.current.clientWidth))
      ) {
        updateScrollMetrics({
          scrollStart: 0,
          scrollSize: 0,
          clientSize: 0,
        })
      } else {
        resetRefSizes()
      }
    }

    // Watch the window resize event
    window.addEventListener('resize', handleResize)

    // Clean up the event listener
    return () => window.removeEventListener('resize', handleResize)
  }, [direction, resetRefSizes, updateScrollMetrics])

  const visibleSides = useMemo((): { start: boolean; end: boolean } => {
    const { scrollStart, scrollSize, clientSize } = scrollMetrics
    const isEnd = clientSize === scrollSize - scrollStart
    const isStart = scrollStart === 0
    const isBetween = scrollStart > 0 && clientSize < scrollSize - scrollStart

    return {
      start: (isEnd || isBetween) && !(isStart && isEnd),
      end: (isStart || isBetween) && !(isStart && isEnd),
    }
  }, [scrollMetrics])

  return (
    <>
      {direction === 'horizontal' ? (
        <div className="relative">
          <div
            data-testid="scroll-shadow-left"
            className={classNames(
              'duration-300$ pointer-events-none absolute inset-y-0 left-0 z-[9] -mr-4 h-full w-4 bg-scroll-shadow-left transition-opacity',
              visibleSides.start ? 'opacity-100' : 'opacity-0'
            )}
          />
          <div
            data-testid={testId || 'scroll-shadow-wrapper'}
            id={id || 'scroll-shadow-wrapper'}
            ref={setWrapperRef}
            style={style}
            className={classNames(
              'relative',
              overflowBothAxes ? 'min-h-0 overflow-auto' : 'overflow-x-auto overflow-y-hidden',
              className,
              {
                'cursor-move': !!dragScrollingEnabled && (visibleSides.end || visibleSides.start),
              }
            )}
            onScroll={onScrollHandler}
            onMouseDown={onMouseDown}
            onMouseLeave={onMouseLeave}
            onMouseUp={onMouseUp}
            onMouseMove={onMouseMove}
          >
            {children}
          </div>
          <div
            data-testid="scroll-shadow-right"
            className={classNames(
              'pointer-events-none absolute inset-y-0 right-0 -ml-4 h-full w-4 bg-scroll-shadow-right transition-opacity duration-300',
              visibleSides.end ? 'opacity-100' : 'opacity-0'
            )}
          />
        </div>
      ) : (
        <div
          data-testid={testId || 'scroll-shadow-wrapper'}
          id={id || 'scroll-shadow-wrapper'}
          ref={setWrapperRef}
          style={style}
          className={classNames('relative overflow-y-auto pr-px', className)}
          onScroll={onScrollHandler}
        >
          <div
            data-testid="scroll-shadow-up"
            className={classNames(
              'pointer-events-none sticky top-0 z-10 -mb-4 h-4 w-full bg-scroll-shadow-up transition-opacity duration-300',
              visibleSides.start ? 'opacity-100' : 'opacity-0'
            )}
          />
          {children}
        </div>
      )}
    </>
  )
})

ScrollShadowWrapper.displayName = 'ScrollShadowWrapper'

export default ScrollShadowWrapper
